import { Card, CardBody, Chip, Image } from '@heroui/react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { Product } from '../types/product';
import { stripHtmlTags } from '../utils/htmlUtils';
import { createProductUrl } from '../utils/seoUtils';

interface ProductCardProps {
  product: Product;
  className?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  className = '',
}) => {
  const { currentLocale, formatCurrency, convertPrice } = useLocale();

  const renderPrice = () => {
    // Preserve backend-computed display (including 'From ' prefix) when available
    // Show as-is for GBP, and preserve the 'From ' prefix for other currencies too
    if (product.priceDisplay) {
      const hasFromPrefix = product.priceDisplay
        .trim()
        .toLowerCase()
        .startsWith('from');
      if (currentLocale.preferredCurrency === 'GBP') {
        return product.priceDisplay;
      }

      // For other currencies, keep the 'From ' prefix if present, but format the numeric part using current currency
      const converted = convertPrice(
        product.startingPrice,
        'GBP',
        currentLocale.preferredCurrency
      );
      const convertedText = formatCurrency(converted);
      return hasFromPrefix ? `From ${convertedText}` : convertedText;
    }

    // Fallback if no priceDisplay provided
    const converted = convertPrice(
      product.startingPrice,
      'GBP',
      currentLocale.preferredCurrency
    );
    return formatCurrency(converted);
  };

  return (
    <Card
      isPressable
      as={Link}
      to={createProductUrl(product.id, product.name)}
      className={`hover:scale-105 transition-transform duration-200 ${className}`}
    >
      <CardBody className='p-0'>
        <div className='relative'>
          {product.mainImageUrl ? (
            <Image
              src={product.mainImageUrl}
              alt={product.name}
              width='100%'
              height={200}
              className='w-full h-48 object-cover'
            />
          ) : (
            <div className='w-full h-48 bg-gray-200 flex items-center justify-center text-4xl'>
              🎲
            </div>
          )}
          {product.isFeatured && (
            <Chip
              size='sm'
              color='warning'
              variant='solid'
              className='absolute top-2 right-2'
            >
              Featured
            </Chip>
          )}
          {product.isBundle && (
            <Chip
              size='sm'
              color='primary'
              variant='solid'
              className='absolute bottom-2 left-2'
            >
              Bundle
            </Chip>
          )}
          {!product.isAvailable && (
            <Chip
              size='sm'
              color='danger'
              variant='solid'
              className='absolute top-2 left-2'
            >
              Out of Stock
            </Chip>
          )}
        </div>

        <div className='p-4'>
          <h3 className='font-semibold text-lg text-gray-900 mb-2 line-clamp-1'>
            {product.name}
          </h3>

          <div className='flex items-center gap-2 mb-2'>
            <Chip size='sm' variant='flat' color='primary'>
              {product.category}
            </Chip>
            <Chip size='sm' variant='flat' color='secondary'>
              {product.material}
            </Chip>
          </div>

          <div className='mb-3 min-h-[40px]'>
            {product.description && (
              <p className='text-sm text-gray-600 line-clamp-2'>
                {stripHtmlTags(product.description)}
              </p>
            )}
            {product.partCount > 0 && (
              <p className='text-xs text-gray-500 mt-1'>
                This product contains {product.partCount}{' '}
                {product.partCount === 1 ? 'part' : 'parts'}
              </p>
            )}
          </div>

          <div className='flex items-center justify-between'>
            <div className='flex flex-col'>
              <span className='text-lg font-bold text-primary'>
                {renderPrice()}
              </span>
              {product.scale && (
                <span className='text-xs text-gray-500'>{product.scale}</span>
              )}
            </div>

            <div className='flex flex-col items-end'>
              <span className='text-xs text-gray-500'>
                {product.stockStatusDisplay}
              </span>
              {product.creator && (
                <span className='text-xs text-gray-400 mt-1'>
                  by {product.creator}
                </span>
              )}
            </div>
          </div>

          {product.dimensionsDisplay && (
            <div className='mt-2 pt-2 border-t border-gray-100'>
              <span className='text-xs text-gray-500'>
                {product.dimensionsDisplay}
              </span>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default ProductCard;
